import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { labelPictureType, sortByOrder, type OmdPicture, type OmdProduct } from '../../../utils/omdProductView';

type OmdImageCarouselProps = {
  product: OmdProduct;
  onOpenLightbox: (index: number) => void;
};

/** Thumbnail strip width + gap to main image (w-24 + gap-2). */
const THUMB_STRIP_CLASS = 'w-24';
const THUMB_SIZE_CLASS = 'w-24 h-24';
const MAIN_THUMB_OFFSET_CLASS = 'mr-[6.75rem]';

export function OmdImageCarousel({ product, onOpenLightbox }: OmdImageCarouselProps) {
  const pictures = sortByOrder((product.pictures ?? []).filter((p) => p?.url || p?.urlThumbnail));
  const [active, setActive] = React.useState(0);

  if (pictures.length === 0) {
    return (
      <div className="aspect-[40/21] rounded-lg border border-app-border bg-app-surface-muted flex items-center justify-center text-xs text-app-text-subtle">
        Kein Produktbild
      </div>
    );
  }

  const current = pictures[active] ?? pictures[0];
  const mainSrc = current.url || current.urlThumbnail;

  const go = (delta: number) => {
    setActive((i) => (i + delta + pictures.length) % pictures.length);
  };

  const hasThumbs = pictures.length > 1;

  return (
    <div className="space-y-2">
      <div className="relative">
        <div
          className={`relative rounded-lg border border-app-border bg-app-surface-muted overflow-hidden group aspect-[40/21] ${
            hasThumbs ? MAIN_THUMB_OFFSET_CLASS : ''
          }`}
        >
          <button
            type="button"
            className="absolute inset-0 block w-full h-full"
            onClick={() => onOpenLightbox(active)}
            aria-label="Bild vergrößern"
          >
            {mainSrc ? (
              <img src={mainSrc} alt="" className="w-full h-full object-contain" />
            ) : null}
          </button>
          {hasThumbs && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-app-surface/90 border border-app-border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Vorheriges Bild"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-app-surface/90 border border-app-border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Nächstes Bild"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {hasThumbs && (
          <div
            className={`absolute right-0 top-0 bottom-0 ${THUMB_STRIP_CLASS} flex flex-col gap-2 overflow-y-auto overscroll-contain`}
            aria-label="Bildvorschau"
          >
            {pictures.map((pic: OmdPicture, idx) => (
              <button
                key={`${pic.url}-${idx}`}
                type="button"
                onClick={() => setActive(idx)}
                className={`shrink-0 ${THUMB_SIZE_CLASS} rounded-md border overflow-hidden ${
                  idx === active ? 'border-green-500 ring-1 ring-green-500/40' : 'border-app-border'
                }`}
              >
                <img
                  src={pic.urlThumbnail || pic.url}
                  alt=""
                  className="w-full h-full object-contain bg-app-surface-muted"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-app-text-subtle truncate">
        {current.description || current.filename || labelPictureType(current.type)}
      </p>
    </div>
  );
}
